import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, EmptyState, Field, Input, PageHeader, Select, Stat, Tabs } from "../components/DesignSystem";
import { MODULE_CATALOG, moduleLabel } from "../domain/modules";
import type { AccountActivitySummary, AdminAccessRequest, AdminAnalyticsSummary, AdminCustomerRecord, AdminInvitationBriefSummary, AdminLeadSummary, AdminOverview, AdminWeddingDetail, AdminWeddingGuestList, AdminWeddingSummary, ApprovalPlanKey, AccessRequestStatus, BetaCohortSummary, ID, InvitationBrief, InvitationBriefRevisionDiffEntry, InvitationBriefRevisionSummary, PlannerTier } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, formatShortDate, LoadingState, useSubmitGuard } from "./common";

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** exponent).toFixed(exponent ? 1 : 0)} ${units[exponent]}`;
}

function BreakdownList({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).filter(([, count]) => count > 0);
  if (entries.length === 0) return <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>No data yet</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      {entries.map(([label, count]) => (
        <div key={label} style={{ display: "flex", justifyContent: "space-between", font: "var(--type-body-sm)" }}>
          <span style={{ color: "var(--text-secondary)" }}>{label}</span>
          <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--text-primary)" }}>{count}</span>
        </div>
      ))}
    </div>
  );
}

function OverviewTab() {
  const { service } = usePlannerData();
  const [data, setData] = React.useState<AdminOverview | null>(null);
  const [analytics, setAnalytics] = React.useState<AdminAnalyticsSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overview, analyticsSummary] = await Promise.all([service.admin.getOverview(), service.admin.getAnalyticsSummary()]);
      setData(overview);
      setAnalytics(analyticsSummary);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState label="Loading overview" />;
  if (error) return <ErrorState message={error} retry={load} />;
  if (!data) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--space-6)" }}>
          <Stat label="Accounts" value={data.totalAccounts} />
          <Stat label="Active weddings" value={data.activeWeddings} caption={`${data.newWeddings} new in last 30 days`} />
          <Stat label="Upcoming" value={data.upcomingWeddings} />
          <Stat label="Past" value={data.pastWeddings} />
          <Stat label="Full plan" value={data.planBreakdown.full} caption={`${data.planBreakdown.custom} on Custom`} />
          <Stat label="Published sites" value={data.publishedWebsites} />
          <Stat label="RSVP responses" value={`${data.rsvpUsage.answered}/${data.rsvpUsage.total}`} />
          <Stat label="Active users" value={data.activeUsers} />
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-6)" }}>
        <Card title="Module adoption" padding="var(--space-5)">
          <BreakdownList data={data.moduleAdoption} />
        </Card>
        <Card title="By country" padding="var(--space-5)">
          <BreakdownList data={data.byCountry} />
        </Card>
        <Card title="By event type" padding="var(--space-5)">
          <BreakdownList data={data.eventTypeBreakdown} />
        </Card>
        <Card title="Guest count bands" padding="var(--space-5)">
          <BreakdownList data={data.guestCountBands} />
        </Card>
      </div>

      {analytics && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-6)" }}>
          <Card title="Event volume, last 30 days" padding="var(--space-5)">
            <BreakdownList data={analytics.eventCountsLast30Days} />
            <div style={{ marginTop: "var(--space-2)", font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{analytics.totalEventsAllTime} events recorded all-time</div>
          </Card>
          <Card title="Acquisition source" padding="var(--space-5)">
            <BreakdownList data={analytics.acquisitionBySource} />
          </Card>
        </div>
      )}

      <Callout tone={data.failedEmails > 0 ? "warning" : "neutral"} title="Operational health">
        {data.failedEmails} failed email(s) · Stationery adoption: {data.stationeryAdoption} wedding(s).
        {" "}Job-queue and storage-failure monitoring aren't instrumented yet, so those figures ({data.failedJobs}/{data.storageFailures}) are placeholders, not measured signals.
      </Callout>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: React.ReactNode }) {
  return <div><span className="pob-eyebrow">{label}</span><div style={{ marginTop: 4, font: "var(--type-label)" }}>{value}</div></div>;
}

function ModuleActionRow({ moduleKey, onRevoke, busy }: { moduleKey: string; onRevoke: () => void; busy: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "var(--space-2) 0" }}>
      <span style={{ font: "var(--type-body-sm)" }}>{moduleLabel(moduleKey)}</span>
      <Button size="sm" variant="ghost" onClick={onRevoke} disabled={busy}>Revoke</Button>
    </div>
  );
}

function WeddingDetailDialog({ weddingId, onClose, onChanged }: { weddingId: ID; onClose: () => void; onChanged: () => void }) {
  const { service } = usePlannerData();
  const [detail, setDetail] = React.useState<AdminWeddingDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionMessage, setActionMessage] = React.useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [grantKey, setGrantKey] = React.useState("");
  const [troubleshoot, setTroubleshoot] = React.useState<string | null>(null);
  const [viewingGuests, setViewingGuests] = React.useState(false);
  const [editingOrder, setEditingOrder] = React.useState(false);
  const [orderForm, setOrderForm] = React.useState({ orderNumber: "", customerName: "", email: "", phone: "" });
  const { submitting, guardedSubmit } = useSubmitGuard();

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await service.admin.getWeddingDetail(weddingId));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service, weddingId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function runAction(label: string, fn: () => Promise<AdminWeddingDetail>): Promise<boolean> {
    let succeeded = false;
    await guardedSubmit(async () => {
      try {
        const updated = await fn();
        setDetail(updated);
        setActionMessage({ tone: "success", text: `${label} succeeded.` });
        onChanged();
        succeeded = true;
      } catch (err) {
        setActionMessage({ tone: "error", text: errorMessage(err) });
      }
    });
    return succeeded;
  }

  const availableToGrant = React.useMemo(() => {
    if (!detail) return [];
    const enabledKeys = new Set(detail.enabledModules.map((m) => m.key));
    return MODULE_CATALOG.filter((m) => !enabledKeys.has(m.key));
  }, [detail]);

  return (
    <Dialog open title={detail ? detail.title : "Wedding detail"} description={detail ? `${detail.slug} · created ${formatDateTime(detail.createdAt)}` : undefined} width={640} onClose={onClose}>
      {loading && <LoadingState label="Loading wedding detail" />}
      {error && <ErrorState message={error} retry={load} />}
      {!loading && !error && detail && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
          {actionMessage && <Callout tone={actionMessage.tone === "success" ? "success" : "error"} title={actionMessage.tone === "success" ? "Done" : "Action failed"}>{actionMessage.text}</Callout>}

          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <Badge tone={detail.status === "active" ? "success" : "warning"}>{detail.status}</Badge>
            <Badge tone={detail.planKey === "full" ? "accent" : "neutral"}>{detail.planKey === "full" ? "Full Planner" : "Custom Planner"}</Badge>
            {detail.websiteStatus && <Badge tone="neutral">Website: {detail.websiteStatus}</Badge>}
          </div>

          <Card title="Couple" padding="var(--space-4)">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)", font: "var(--type-body-sm)" }}>
              <span>{detail.preferences?.partnerAName} &amp; {detail.preferences?.partnerBName}</span>
              <span>{[detail.preferences?.city, detail.preferences?.country].filter(Boolean).join(", ") || "—"}</span>
              <span>{detail.preferences?.dateStart ? formatShortDate(detail.preferences.dateStart.slice(0, 10)) : "No date set"}</span>
              <span>{detail.preferences?.guestCountEstimate ? `${detail.preferences.guestCountEstimate} guests (est.)` : "—"}</span>
            </div>
          </Card>

          <Card title={`Collaborators (${detail.collaborators.length})`} padding="var(--space-4)">
            {detail.collaborators.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>None yet</span>}
            {detail.collaborators.map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "var(--space-1) 0", font: "var(--type-body-sm)" }}>
                <span>{c.name || c.email || "Unknown"}</span>
                <span style={{ color: "var(--text-tertiary)" }}>{c.role}</span>
              </div>
            ))}
          </Card>

          <Card title="Modules" padding="var(--space-4)">
            {detail.enabledModules.map((m) => (
              <ModuleActionRow key={m.key} moduleKey={m.key} busy={submitting} onRevoke={() => runAction(`Revoke ${moduleLabel(m.key)}`, () => service.admin.revokeModule(weddingId, m.key))} />
            ))}
            {availableToGrant.length > 0 && (
              <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
                <Select size="sm" value={grantKey} placeholder="Choose a module to grant" options={availableToGrant.map((m) => ({ value: m.key, label: m.label }))} onChange={(e) => setGrantKey(e.target.value)} />
                <Button size="sm" disabled={!grantKey || submitting} onClick={() => runAction("Grant module", () => service.admin.grantModule(weddingId, grantKey))}>Grant</Button>
              </div>
            )}
          </Card>

          <Card title="Plan" padding="var(--space-4)">
            <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>
                Currently {detail.planKey === "full" ? "Full Planner (all modules)" : detail.planKey === "pob_client" ? "Pockets of Beige Client (complimentary)" : "Custom Planner"}.
              </span>
              {detail.planKey !== "full" && (
                <Button size="sm" variant="secondary" disabled={submitting} onClick={() => runAction("Grant Full Planner", () => service.admin.changePlan(weddingId, "full" as PlannerTier))}>Grant Full Planner</Button>
              )}
              {detail.planKey === "full" && (
                <Button size="sm" variant="secondary" disabled={submitting} onClick={() => runAction("Switch to Custom Planner", () => service.admin.changePlan(weddingId, "custom" as PlannerTier, detail.enabledModules.map((m) => m.key)))}>Switch to Custom</Button>
              )}
            </div>
          </Card>

          <Card title="Access" padding="var(--space-4)">
            <div className="pob-grid-2" style={{ marginBottom: "var(--space-3)" }}>
              <Summary label="Order #" value={detail.access.orderNumber || "—"} />
              <Summary label="Customer name" value={detail.access.customerName || "—"} />
              <Summary label="Activation" value={detail.access.activationStatus === "activated" ? "Activated customer" : "Direct sign-up"} />
              {detail.access.accessRequestStatus && <Summary label="Access request" value={detail.access.accessRequestStatus} />}
              <Summary label="Private Beta" value={detail.access.beta?.status === "ACTIVE" ? `Active${detail.access.beta.cohortNumber ? ` · cohort ${detail.access.beta.cohortNumber}` : ""}` : "Not granted"} />
            </div>
            {editingOrder ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", marginBottom: "var(--space-3)" }}>
                <div className="pob-grid-2">
                  <Field label="Order number"><Input value={orderForm.orderNumber} onChange={(e) => setOrderForm({ ...orderForm, orderNumber: e.target.value })} /></Field>
                  <Field label="Customer name"><Input value={orderForm.customerName} onChange={(e) => setOrderForm({ ...orderForm, customerName: e.target.value })} /></Field>
                  <Field label="Email" hint="Email or phone required"><Input type="email" value={orderForm.email} onChange={(e) => setOrderForm({ ...orderForm, email: e.target.value })} /></Field>
                  <Field label="Phone" hint="Email or phone required"><Input value={orderForm.phone} onChange={(e) => setOrderForm({ ...orderForm, phone: e.target.value })} /></Field>
                </div>
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={submitting || !orderForm.orderNumber || !orderForm.customerName || !(orderForm.email || orderForm.phone)}
                    onClick={() =>
                      runAction("Save order info", () =>
                        service.admin.setCustomerRecord(weddingId, {
                          orderNumber: orderForm.orderNumber,
                          customerName: orderForm.customerName,
                          email: orderForm.email || undefined,
                          phone: orderForm.phone || undefined
                        })
                      ).then((succeeded) => { if (succeeded) setEditingOrder(false); })
                    }
                  >
                    Save order info
                  </Button>
                  <Button size="sm" onClick={() => setEditingOrder(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", marginBottom: "var(--space-3)" }}>
                <Button
                  size="sm"
                  onClick={() => {
                    setOrderForm({
                      orderNumber: detail.access.orderNumber || "",
                      customerName: detail.access.customerName || "",
                      email: detail.access.customerEmail || "",
                      phone: detail.access.customerPhone || ""
                    });
                    setEditingOrder(true);
                  }}
                >
                  {detail.access.orderNumber ? "Edit order info" : "Add order info"}
                </Button>
              </div>
            )}
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              {detail.access.beta?.status !== "ACTIVE" && (
                <Button size="sm" variant="secondary" disabled={submitting} onClick={() => runAction("Grant Beta Full", () => service.admin.grantBeta(weddingId))}>Grant Beta Full</Button>
              )}
              {detail.access.beta?.status === "ACTIVE" && (
                <Button size="sm" variant="danger" disabled={submitting} onClick={() => runAction("Revoke Beta Full", () => service.admin.revokeBeta(weddingId))}>Revoke Beta Full</Button>
              )}
            </div>
          </Card>

          <Card title="Account" padding="var(--space-4)">
            <div style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)", marginBottom: "var(--space-3)" }}>Storage used: {formatBytes(detail.storageBytes)}</div>
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              {detail.status === "active" ? (
                <Button size="sm" variant="danger" disabled={submitting} onClick={() => runAction("Disable account", () => service.admin.disableAccount(weddingId))}>Disable account</Button>
              ) : (
                <Button size="sm" variant="secondary" disabled={submitting} onClick={() => runAction("Restore account", () => service.admin.restoreAccount(weddingId))}>Restore account</Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                disabled={submitting}
                onClick={() =>
                  guardedSubmit(async () => {
                    try {
                      const result = await service.admin.resendVerification(weddingId);
                      setActionMessage({ tone: "success", text: result.sent ? `Verification email sent (${result.status}).` : `Not sent — ${result.reason === "already_verified" ? "owner is already verified." : result.reason}` });
                    } catch (err) {
                      setActionMessage({ tone: "error", text: errorMessage(err) });
                    }
                  })
                }
              >
                Resend verification
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={submitting}
                onClick={() =>
                  guardedSubmit(async () => {
                    try {
                      const result = await service.admin.troubleshootWebsite(weddingId);
                      setTroubleshoot(result.exists ? `Site "${result.slug}" is ${result.status}${result.publicUrlPath ? ` at ${result.publicUrlPath}` : ""}.` : "No website has been created for this wedding yet.");
                    } catch (err) {
                      setTroubleshoot(errorMessage(err));
                    }
                  })
                }
              >
                Troubleshoot website
              </Button>
              <Button size="sm" variant="secondary" icon="users" onClick={() => setViewingGuests(true)}>View guest list</Button>
            </div>
            {troubleshoot && <Callout tone="neutral" title="Website diagnostic" style={{ marginTop: "var(--space-3)" }}>{troubleshoot}</Callout>}
          </Card>
          {viewingGuests && <AdminGuestListDialog weddingId={weddingId} onClose={() => setViewingGuests(false)} />}

          <Callout tone="neutral" title="Recent activity">
            {detail.recentActivity.length === 0 ? "No activity recorded yet." : detail.recentActivity.slice(0, 5).map((a) => `${a.who}: ${a.text}`).join(" · ")}
          </Callout>
        </div>
      )}
    </Dialog>
  );
}

function WeddingsTab() {
  const { service } = usePlannerData();
  const [rows, setRows] = React.useState<AdminWeddingSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedId, setSelectedId] = React.useState<ID | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await service.admin.listWeddings());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      {loading && <LoadingState label="Loading admin list" />}
      {error && <ErrorState message={error} retry={load} />}
      {!loading && !error && rows.length === 0 && <Card><EmptyState icon="shield-check" title="No weddings found" description="Admin access succeeded, but there are no customer records yet." /></Card>}
      {!loading && !error && rows.length > 0 && (
        <Card padding="0">
          <DataTable
            rows={rows}
            onRowClick={(row) => setSelectedId(row.id)}
            columns={[
              { key: "title", header: "Wedding", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.title}</span> },
              { key: "customerName", header: "Customer", muted: true, render: (row) => row.customerName || "-" },
              { key: "orderNumber", header: "Order #", muted: true, render: (row) => row.orderNumber || "-" },
              { key: "ownerEmail", header: "Owner", muted: true, render: (row) => row.ownerEmail || "-" },
              {
                key: "invitationBriefStatus",
                header: "Invitation Brief",
                render: (row) => (
                  <Badge tone={row.invitationBriefStatus === "submitted" ? "success" : row.invitationBriefStatus === "draft" ? "accent" : "neutral"}>
                    {row.invitationBriefStatus === "submitted" ? "Submitted" : row.invitationBriefStatus === "draft" ? "Draft" : "Not started"}
                  </Badge>
                )
              },
              {
                key: "plannerAccess",
                header: "Planner access",
                render: (row) => (
                  <Badge tone={row.planKey === "full" ? "accent" : "neutral"}>
                    {row.planKey === "pob_client" ? "POB Client" : row.planKey === "custom" ? "Custom" : "Full"}{row.betaActive ? " + Beta" : ""}
                  </Badge>
                )
              },
              { key: "enabledModules", header: "Modules", align: "right", render: (row) => row.enabledModules.length },
              { key: "collaboratorCount", header: "Team", align: "right", render: (row) => row.collaboratorCount },
              { key: "location", header: "Location", muted: true, render: (row) => [row.city, row.country].filter(Boolean).join(", ") || "-" },
              { key: "lastActivityAt", header: "Last activity", muted: true, render: (row) => formatDateTime(row.lastActivityAt) },
              { key: "status", header: "Status", render: (row) => <Badge tone={row.status === "active" ? "success" : "warning"}>{row.status}</Badge> }
            ]}
          />
        </Card>
      )}
      {selectedId && <WeddingDetailDialog weddingId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />}
    </div>
  );
}

function LeadsTab() {
  const { service } = usePlannerData();
  const [leads, setLeads] = React.useState<AdminLeadSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLeads(await service.admin.listLeads());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState label="Loading leads" />;
  if (error) return <ErrorState message={error} retry={load} />;
  if (leads.length === 0) return <Card><EmptyState icon="sparkle" title="No quote requests yet" description="Leads submitted from Stationery/Gifts will appear here." /></Card>;

  return (
    <Card padding="0">
      <DataTable
        rows={leads}
        columns={[
          { key: "weddingTitle", header: "Wedding", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.weddingTitle}</span> },
          { key: "requestedItems", header: "Requested", wrap: true, render: (row) => row.requestedItems.join(", ") },
          { key: "eventCount", header: "Events", align: "right", render: (row) => row.eventCount },
          { key: "invitationQuantity", header: "Invitations", align: "right", render: (row) => row.invitationQuantity ?? "—" },
          { key: "guestHouseholdQuantity", header: "Households", align: "right", render: (row) => row.guestHouseholdQuantity ?? "—" },
          { key: "location", header: "Location", muted: true, render: (row) => row.location || "—" },
          { key: "status", header: "Status", render: (row) => <Badge tone={row.status === "pending" ? "accent" : row.status === "closed" ? "neutral" : "success"}>{row.status}</Badge> },
          { key: "createdAt", header: "Requested", muted: true, render: (row) => formatDateTime(row.createdAt) }
        ]}
      />
    </Card>
  );
}

/** Which account logged in when and how long they stuck around -- reconstructed server-side from
 * heartbeat pings (see AdminService.getAccountActivity), not an exact stopwatch. */
function AccountActivityTab() {
  const { service } = usePlannerData();
  const [rows, setRows] = React.useState<AccountActivitySummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [detailFor, setDetailFor] = React.useState<AccountActivitySummary | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await service.admin.getAccountActivity());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState label="Loading account activity" />;
  if (error) return <ErrorState message={error} retry={load} />;
  if (rows.length === 0) return <Card><EmptyState icon="clock" title="No accounts yet" description="Login activity will appear here once accounts start signing in." /></Card>;

  return (
    <>
      <Card padding="0">
        <DataTable
          rows={rows}
          columns={[
            { key: "account", header: "Account", render: (row) => (
              <div>
                <div style={{ font: "var(--type-label)" }}>{row.customerName || row.name || row.email}</div>
                <div style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{row.email}{row.orderNumber ? ` · #${row.orderNumber}` : ""}</div>
              </div>
            ) },
            { key: "lastSeenAt", header: "Last seen", muted: true, render: (row) => formatDateTime(row.lastSeenAt) },
            { key: "totalVisits", header: "Visits", align: "right", render: (row) => row.totalVisits },
            { key: "totalTimeSeconds", header: "Total time", align: "right", render: (row) => formatDuration(row.totalTimeSeconds) },
            { key: "detail", header: "", align: "right", render: (row) => <Button size="sm" disabled={row.recentVisits.length === 0} onClick={() => setDetailFor(row)}>View sessions</Button> }
          ]}
        />
      </Card>
      {detailFor && (
        <Dialog open title={detailFor.customerName || detailFor.name || detailFor.email} description={`${detailFor.email} · ${detailFor.totalVisits} visit${detailFor.totalVisits === 1 ? "" : "s"} · ${formatDuration(detailFor.totalTimeSeconds)} total`} width={520} onClose={() => setDetailFor(null)}>
          <DataTable
            rows={detailFor.recentVisits.map((v, i) => ({ ...v, id: `${detailFor.userId}-${i}` }))}
            columns={[
              { key: "startedAt", header: "Logged in", render: (row) => formatDateTime(row.startedAt) },
              { key: "durationSeconds", header: "Time spent", align: "right", render: (row) => formatDuration(row.durationSeconds) }
            ]}
          />
        </Dialog>
      )}
    </>
  );
}

const RSVP_BADGE: Record<string, { tone: "neutral" | "success" | "error"; label: string }> = {
  PENDING: { tone: "neutral", label: "Pending" },
  ATTENDING: { tone: "success", label: "Yes" },
  DECLINED: { tone: "error", label: "No" }
};

/** A single wedding's guest list for POB staff -- read-only; every open is audited server-side
 * as a sensitive read (docs/DECISIONS.md L-028). */
function AdminGuestListDialog({ weddingId, onClose }: { weddingId: ID; onClose: () => void }) {
  const { service } = usePlannerData();
  const [list, setList] = React.useState<AdminWeddingGuestList | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");

  const load = React.useCallback(async () => {
    setError(null);
    try {
      setList(await service.admin.getWeddingGuestList(weddingId));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [service, weddingId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = (list?.guests || []).filter((guest) => !query || `${guest.fullName} ${guest.household || ""} ${guest.group || ""}`.toLowerCase().includes(query));
  return (
    <Dialog open title={list ? `${list.weddingTitle} — guest list` : "Guest list"} description={list ? `${list.guests.length} guest${list.guests.length === 1 ? "" : "s"} · ${list.householdCount} household${list.householdCount === 1 ? "" : "s"} · read-only, and every view is logged` : undefined} width={960} onClose={onClose}>
      {error && <ErrorState message={error} retry={load} />}
      {!list && !error && <LoadingState label="Loading guest list" />}
      {list && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <Input icon="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search guests, households or groups" />
          <Card padding="0">
            <DataTable
              dense
              rows={rows}
              emptyLabel={query ? `No guests match "${search}"` : "This wedding has no guests yet."}
              columns={[
                { key: "fullName", header: "Guest", render: (guest) => <span style={{ font: "var(--type-label)" }}>{guest.fullName}</span> },
                { key: "household", header: "Household", muted: true, render: (guest) => guest.household || "-" },
                { key: "group", header: "Group", muted: true, render: (guest) => guest.group || "-" },
                { key: "phone", header: "Phone", muted: true, render: (guest) => guest.phone || "-" },
                { key: "email", header: "Email", muted: true, render: (guest) => guest.email || "-" },
                ...list.events.map((event) => ({
                  key: `event-${event.id}`,
                  header: event.name,
                  render: (guest: AdminWeddingGuestList["guests"][number]) => {
                    const badge = RSVP_BADGE[guest.invitations[event.id] ?? ""];
                    return badge ? <Badge dot tone={badge.tone}>{badge.label}</Badge> : <span style={{ color: "var(--text-tertiary)" }}>-</span>;
                  }
                }))
              ]}
            />
          </Card>
        </div>
      )}
    </Dialog>
  );
}

function InvitationBriefDetailDialog({ weddingId, onClose }: { weddingId: ID; onClose: () => void }) {
  const { service } = usePlannerData();
  const [draft, setDraft] = React.useState<InvitationBrief | null>(null);
  const [revisions, setRevisions] = React.useState<InvitationBriefRevisionSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState<number | null>(null);
  const [diffFor, setDiffFor] = React.useState<number | null>(null);
  const [diff, setDiff] = React.useState<InvitationBriefRevisionDiffEntry[]>([]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await service.admin.getInvitationBriefDetail(weddingId);
      setDraft(detail.draft);
      setRevisions(detail.revisions);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service, weddingId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const download = async (version: number) => {
    setDownloading(version);
    try {
      const url = await service.admin.getInvitationBriefPdfUrl(weddingId, version);
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(null);
    }
  };

  const viewDiff = async (version: number) => {
    if (diffFor === version) {
      setDiffFor(null);
      return;
    }
    const entries = await service.admin.diffInvitationBriefRevisions(weddingId, version, version - 1);
    setDiff(entries);
    setDiffFor(version);
  };

  return (
    <Dialog open title={draft ? `${draft.answers.couple.brideFullName || ""} & ${draft.answers.couple.groomFullName || ""}` : "Invitation Brief"} description={draft?.orderNumber ? `Order #${draft.orderNumber}` : undefined} width={640} onClose={onClose}>
      {loading && <LoadingState label="Loading invitation brief" />}
      {error && <ErrorState message={error} retry={load} />}
      {!loading && !error && draft && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <Badge tone={draft.status === "SUBMITTED" ? "success" : "warning"}>{draft.status}</Badge>
            <Badge tone="neutral">Version {draft.currentVersion}</Badge>
            {draft.customerEmail && <Badge tone="neutral">{draft.customerEmail}</Badge>}
          </div>

          <Card title="Celebrations" padding="var(--space-4)">
            {(draft.answers.celebrations || []).length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>None yet</span>}
            {(draft.answers.celebrations || []).map((c) => (
              <div key={c.label} style={{ display: "flex", justifyContent: "space-between", padding: "var(--space-1) 0", font: "var(--type-body-sm)" }}>
                <span>{c.label}</span>
                <span style={{ color: "var(--text-tertiary)" }}>{c.date} {c.venueName}</span>
              </div>
            ))}
          </Card>

          <Card title={`Versions (${revisions.length})`} padding="0">
            {revisions.length === 0 && <div className="pob-pad" style={{ color: "var(--text-tertiary)" }}>Not submitted yet.</div>}
            {revisions.map((rev, i) => (
              <div key={rev.id} style={{ padding: "var(--space-3) var(--space-4)", borderTop: i ? "1px solid var(--divider)" : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ font: "var(--type-body-sm)" }}>v{rev.version} · {formatDateTime(rev.submittedAt)} · {rev.submittedByLabel}</span>
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    {rev.version > 1 && <Button size="sm" onClick={() => void viewDiff(rev.version)}>{diffFor === rev.version ? "Hide" : "What changed"}</Button>}
                    <Button size="sm" disabled={downloading === rev.version} onClick={() => void download(rev.version)}>{downloading === rev.version ? "..." : "PDF"}</Button>
                  </div>
                </div>
                {diffFor === rev.version && (
                  <div style={{ marginTop: "var(--space-2)", display: "flex", flexDirection: "column", gap: 2 }}>
                    {diff.length === 0 && <span style={{ color: "var(--text-tertiary)", font: "var(--type-body-sm)" }}>No differences.</span>}
                    {diff.map((d) => <span key={d.field} style={{ font: "var(--type-body-sm)" }}>{d.field}: {d.from || "(empty)"} → {d.to || "(empty)"}</span>)}
                  </div>
                )}
              </div>
            ))}
          </Card>
        </div>
      )}
    </Dialog>
  );
}

function InvitationBriefsTab() {
  const { service } = usePlannerData();
  const [rows, setRows] = React.useState<AdminInvitationBriefSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedId, setSelectedId] = React.useState<ID | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await service.admin.listInvitationBriefs());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState label="Loading invitation briefs" />;
  if (error) return <ErrorState message={error} retry={load} />;
  if (rows.length === 0) return <Card><EmptyState icon="mail" title="No invitation briefs yet" description="Briefs submitted by customers will appear here." /></Card>;

  return (
    <>
      <Card padding="0">
        <DataTable
          rows={rows.map((row) => ({ ...row, id: row.weddingId }))}
          onRowClick={(row) => setSelectedId(row.weddingId)}
          columns={[
            { key: "weddingTitle", header: "Wedding", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.weddingTitle}</span> },
            { key: "orderNumber", header: "Order #", muted: true, render: (row) => row.orderNumber || "—" },
            { key: "customerName", header: "Customer", muted: true, render: (row) => row.customerName || "—" },
            { key: "invitationProduct", header: "Invitation / design", muted: true, render: (row) => row.invitationProduct || "—" },
            { key: "status", header: "Status", render: (row) => <Badge tone={row.status === "SUBMITTED" ? "success" : "warning"}>{row.status}</Badge> },
            { key: "currentVersion", header: "Version", align: "right", render: (row) => row.currentVersion },
            { key: "submittedAt", header: "Submitted", muted: true, render: (row) => formatDateTime(row.submittedAt) }
          ]}
        />
      </Card>
      {selectedId && <InvitationBriefDetailDialog weddingId={selectedId} onClose={() => setSelectedId(null)} />}
    </>
  );
}

export function AdminScreen() {
  const [tab, setTab] = React.useState<"overview" | "weddings" | "leads" | "invitation-briefs" | "access-requests" | "activity">("overview");

  return (
    <div>
      <PageHeader
        eyebrow="POB Admin"
        title="Admin"
        meta={[{ icon: "shield-check", label: "Sensitive reads are audited server-side" }]}
        tabs={<Tabs tabs={[{ value: "overview", label: "Overview" }, { value: "weddings", label: "Weddings" }, { value: "leads", label: "Leads" }, { value: "invitation-briefs", label: "Invitation Briefs" }, { value: "access-requests", label: "Access Requests" }, { value: "activity", label: "Account Activity" }]} value={tab} onChange={setTab} />}
      />
      <div className="pob-pad-wide">
        {tab === "overview" && <OverviewTab />}
        {tab === "weddings" && <WeddingsTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "invitation-briefs" && <InvitationBriefsTab />}
        {tab === "access-requests" && <AccessRequestsTab />}
        {tab === "activity" && <AccountActivityTab />}
      </div>
    </div>
  );
}

/** Support customer records (§1) -- hand-entered until Shopify order provisioning lands. Each
 * record's order number is the one-time activation credential a customer uses to claim their
 * complimentary POB Client wedding at /activation. */
function CustomerRecordsCard() {
  const { service } = usePlannerData();
  const [rows, setRows] = React.useState<AdminCustomerRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<ID | null>(null);
  const [input, setInput] = React.useState({ orderNumber: "", customerName: "", email: "", phone: "", invitationProduct: "" });
  const { submitting, guardedSubmit } = useSubmitGuard();

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await service.admin.listCustomerRecords());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const closeForm = () => {
    setOpen(false);
    setEditingId(null);
    setInput({ orderNumber: "", customerName: "", email: "", phone: "", invitationProduct: "" });
  };

  const startEdit = (row: AdminCustomerRecord) => {
    setInput({ orderNumber: row.orderNumber, customerName: row.customerName, email: row.email || "", phone: row.phone || "", invitationProduct: row.invitationProduct || "" });
    setEditingId(row.id);
    setOpen(true);
  };

  const save = () =>
    guardedSubmit(async () => {
      const payload = {
        orderNumber: input.orderNumber,
        customerName: input.customerName,
        email: input.email || undefined,
        phone: input.phone || undefined,
        invitationProduct: input.invitationProduct || undefined
      };
      if (editingId) await service.admin.updateCustomerRecord(editingId, payload);
      else await service.admin.createCustomerRecord(payload);
      closeForm();
      await load();
    });

  return (
    <Card title="POB customer orders" action={<Button size="sm" onClick={() => (open ? closeForm() : setOpen(true))}>{open ? "Cancel" : "Add order"}</Button>} padding="var(--space-4)">
      {error && <Callout tone="error" title="Couldn't load orders">{error}</Callout>}
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", marginBottom: "var(--space-4)" }}>
          <div className="pob-grid-2">
            <Field label="Order number"><Input value={input.orderNumber} onChange={(e) => setInput({ ...input, orderNumber: e.target.value })} /></Field>
            <Field label="Customer name"><Input value={input.customerName} onChange={(e) => setInput({ ...input, customerName: e.target.value })} /></Field>
            <Field label="Email" hint="Email or phone required"><Input type="email" value={input.email} onChange={(e) => setInput({ ...input, email: e.target.value })} /></Field>
            <Field label="Phone" hint="Email or phone required"><Input value={input.phone} onChange={(e) => setInput({ ...input, phone: e.target.value })} /></Field>
            <Field label="Invitation / product" hint="Optional"><Input value={input.invitationProduct} onChange={(e) => setInput({ ...input, invitationProduct: e.target.value })} /></Field>
          </div>
          <Button variant="primary" size="sm" disabled={!input.orderNumber || !input.customerName || !(input.email || input.phone) || submitting} onClick={save}>{submitting ? "Saving..." : editingId ? "Save changes" : "Save order"}</Button>
        </div>
      )}
      {loading ? <LoadingState label="Loading orders" /> : rows.length === 0 ? (
        <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>No orders yet.</span>
      ) : (
        <DataTable
          rows={rows.map((r) => ({ ...r, id: r.id }))}
          columns={[
            { key: "orderNumber", header: "Order #", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.orderNumber}</span> },
            { key: "customerName", header: "Customer", muted: true },
            { key: "email", header: "Email", muted: true, render: (row) => row.email || row.phone || "—" },
            { key: "invitationProduct", header: "Product", muted: true, render: (row) => row.invitationProduct || "—" },
            { key: "activationConsumedAt", header: "Status", render: (row) => <Badge tone={row.activationConsumedAt ? "success" : "accent"}>{row.activationConsumedAt ? "Activated" : "Not yet activated"}</Badge> },
            { key: "edit", header: "", align: "right", render: (row) => <Button size="sm" onClick={() => startEdit(row)}>Edit</Button> }
          ]}
        />
      )}
    </Card>
  );
}

function AccessRequestsTab() {
  const { service } = usePlannerData();
  const [rows, setRows] = React.useState<AdminAccessRequest[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<AccessRequestStatus | "">("PENDING_APPROVAL");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [cohort, setCohort] = React.useState<BetaCohortSummary | null>(null);
  const [approvingId, setApprovingId] = React.useState<ID | null>(null);
  const [approvePlan, setApprovePlan] = React.useState<ApprovalPlanKey>("pob_client");
  const { submitting, guardedSubmit } = useSubmitGuard();

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, cohortSummary] = await Promise.all([service.admin.listAccessRequests(statusFilter || undefined), service.admin.getBetaCohortSummary()]);
      setRows(list);
      setCohort(cohortSummary);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service, statusFilter]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const approve = (id: ID) =>
    guardedSubmit(async () => {
      await service.admin.approveAccessRequest(id, approvePlan);
      setApprovingId(null);
      await load();
    });

  const reject = (id: ID) =>
    guardedSubmit(async () => {
      await service.admin.rejectAccessRequest(id);
      await load();
    });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
      {cohort && (
        <Card padding="var(--space-4)">
          <span className="pob-eyebrow">Private Beta</span>
          <div style={{ font: "var(--type-title)", marginTop: 4 }}>{cohort.activeCount} / {cohort.maxCohort} places allocated</div>
        </Card>
      )}
      <CustomerRecordsCard />
      <Tabs
        tabs={[
          { value: "PENDING_APPROVAL", label: "Pending" },
          { value: "APPROVED", label: "Approved" },
          { value: "REJECTED", label: "Rejected" }
        ]}
        value={statusFilter || "PENDING_APPROVAL"}
        onChange={(value) => setStatusFilter(value as AccessRequestStatus)}
      />
      {loading && <LoadingState label="Loading access requests" />}
      {error && <ErrorState message={error} retry={load} />}
      {!loading && !error && rows.length === 0 && <Card><EmptyState icon="inbox" title="Nothing here" description="No access requests with this status yet." /></Card>}
      {!loading && !error && rows.length > 0 && (
        <Card padding="0">
          {rows.map((row, i) => (
            <div key={row.id} style={{ padding: "var(--space-4)", borderTop: i ? "1px solid var(--divider)" : 0, display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
                <div>
                  <div style={{ font: "var(--type-label)" }}>{row.name}</div>
                  <div style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{row.email} {row.country ? `· ${row.country}` : ""}</div>
                  {row.message && <div style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)", marginTop: 4 }}>{row.message}</div>}
                </div>
                <Badge tone={row.status === "PENDING_APPROVAL" ? "accent" : row.status === "REJECTED" ? "neutral" : "success"}>{row.status}</Badge>
              </div>
              {row.status === "PENDING_APPROVAL" && (
                approvingId === row.id ? (
                  <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
                    <Select
                      size="sm"
                      value={approvePlan}
                      options={[{ value: "pob_client", label: "POB Client" }, { value: "custom", label: "Custom" }, { value: "full", label: "Full" }, { value: "beta_full", label: "Beta Full" }]}
                      onChange={(e) => setApprovePlan(e.target.value as ApprovalPlanKey)}
                    />
                    <Button size="sm" variant="primary" disabled={submitting} onClick={() => approve(row.id)}>Confirm approval</Button>
                    <Button size="sm" onClick={() => setApprovingId(null)}>Cancel</Button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    <Button size="sm" variant="primary" onClick={() => setApprovingId(row.id)}>Approve</Button>
                    <Button size="sm" variant="danger" disabled={submitting} onClick={() => reject(row.id)}>Reject</Button>
                  </div>
                )
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
